import { useEffect, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { FileText, Download, FileSpreadsheet, FileImage } from "lucide-react";
import { format, parseISO } from "date-fns";
import { useForceDark } from "@/hooks/useForceDark";

const humanSize = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const iconFor = (contentType) => {
  if (contentType?.includes("spreadsheet") || contentType?.includes("csv")) return FileSpreadsheet;
  if (contentType?.startsWith("image/")) return FileImage;
  return FileText;
};

export default function PublicDocuments() {
  useForceDark();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { (async () => {
    try {
      // axios instance sends Authorization if there's a token; drop it explicitly for cleanliness
      const { data } = await api.get("/public/documents", { headers: { Authorization: "" } });
      setDocs(data);
    } finally { setLoading(false); }
  })(); }, []);

  const groups = ["Policies", "Handbooks", "Welfare", "Forms", "Other", "Uncategorised"].map(cat => ({
    cat,
    items: docs.filter(d => (d.category || "Uncategorised") === cat),
  })).filter(g => g.items.length > 0);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] text-foreground">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="mb-10">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Elsenham Youth FC · Public handbook</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-3">Club documents</h1>
          <p className="opacity-60 mt-3 text-sm">Policies, welfare guidance and downloadable forms shared publicly by the committee. No login required.</p>
        </div>

        {loading ? (
          <div className="opacity-50 font-display tracking-widest text-sm">LOADING…</div>
        ) : docs.length === 0 ? (
          <div className="opacity-50 text-sm">Nothing has been published yet. Please check back soon.</div>
        ) : (
          <div className="space-y-8" data-testid="public-documents-list">
            {groups.map(g => (
              <div key={g.cat}>
                <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-3">{g.cat}</div>
                <div className="space-y-2">
                  {g.items.map(d => {
                    const Icon = iconFor(d.content_type);
                    return (
                      <a
                        key={d.id}
                        href={`${BACKEND_URL}/api/public/documents/${d.id}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 p-4 rounded border border-border bg-muted/40 hover:border-[#83B68B]/60 transition-colors group"
                        data-testid={`public-doc-${d.id}`}
                      >
                        <Icon className="w-5 h-5 text-[#83B68B] flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium group-hover:text-[#83B68B]">{d.title}</div>
                          <div className="text-[10px] opacity-50 mt-0.5">
                            {humanSize(d.size)}{d.created_at ? ` · added ${format(parseISO(d.created_at), "d MMM yyyy")}` : ""}
                          </div>
                        </div>
                        <Download className="w-4 h-4 opacity-40 group-hover:opacity-100 group-hover:text-[#83B68B]" />
                      </a>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-16 pt-8 border-t border-border text-[10px] tracking-widest uppercase opacity-40 text-center">
          <a href="/" className="hover:text-[#83B68B]">← Elsenham Youth FC</a>
        </div>
      </div>
    </div>
  );
}
